import { Spacer, Text } from "@hackthenorth/north";
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid";
import { GraphQLError } from "graphql";
import { Alert, Button, Spinner, TextInput } from "north.js";
import React, { useEffect, useRef, useState } from "react";
import { Navigate, Prompt } from "react-router-dom";
import styled from "styled-components";
import "twin.macro";

import {
  FormFieldLabelRow,
  Header,
  OptionalLabel,
  PageWrapper,
} from "src/shared/components";
import { successToast, errorToast } from "src/shared/components/Toast";
import { Route } from "src/shared/constants/route";
import {
  ProfileContextProvider,
  useHackerContext,
  useNetworkingContext,
  useProfileContext,
  useUserContext,
} from "src/shared/contexts";
import { Field, PageStage } from "src/shared/contexts/HackerContext/types";
import { useHackerState } from "src/shared/contexts/HackerContext/useHackerState";
import { useDeviceSize, useIsMounted, useRsvpFlow } from "src/shared/hooks";
import {
  isEmail,
  isLatinOnly,
  isPhoneNumber,
  isValidDiscordTag,
  isValidSocialMediaURL,
} from "src/shared/utils/validation";
import { formatValidationIssues } from "src/shared/utils/validationMessages";
import { ArrowRight } from "src/static/icons";

import {
  buildInitialDirectoryProfileSeed,
  shouldInitializeDirectoryProfile,
} from "../profile/prefill";
import RSVPSidebar from "../rsvp-sidebar";

import { shouldShowBadgeSupplyWarning } from "./availability";
import {
  ACCEPTED_FIELDS,
  isValidBadgeSocialURL,
  LATIN_ONLY_MESSAGE,
} from "./constants";
import { BadgeNetworkingAnswers, buildBadgeNetworkingValues } from "./prefill";

const NETWORKING_PREFILL_FIELDS = [
  Field.NETWORKING_PHONE,
  Field.NETWORKING_EMAIL,
  Field.LINKEDIN,
  Field.INSTAGRAM,
  Field.TWITTER,
  Field.DISCORD_TAG,
] as const;

type BadgePageProps = {
  variant?: "rsvp" | "dashboard";
};

const BadgePageContent: React.FC<BadgePageProps> = ({ variant = "rsvp" }) => {
  const isDashboard = variant === "dashboard";
  const isTabletOrSmaller = useDeviceSize("tablet");
  const isMounted = useIsMounted();
  const { isHacker, isVolunteer } = useRsvpFlow();
  const { info, isOrganizer, roles } = useUserContext();
  const {
    claimData,
    updateResponses,
    isLoading: isHackerLoading,
    navigateNext,
    navigateBack,
    queueAutosave,
    cancelAutosave,
    waitForAutosave,
    hasUnsavedAutosave,
  } = useHackerContext();
  const {
    extendedProfile,
    updateExtendedProfile,
    autosaveExtendedProfile,
    isLoading: isNetworkingLoading,
  } = useNetworkingContext();
  const {
    profile: directoryProfile,
    isLoading: isProfileLoading,
    loadError: directoryProfileLoadError,
    isUpdating: isProfileUpdating,
    upsertDiscoveryProfile,
  } = useProfileContext();

  const { responsesState, setResponsesState, isValid, isResponseUpdated } =
    useHackerState(ACCEPTED_FIELDS, {});

  const [showErrors, setShowErrors] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [badgeDisplayName, setBadgeDisplayName] = useState("");
  const [hasInitializedBadgeName, setHasInitializedBadgeName] = useState(false);
  const hasSeededPrefill = useRef(false);
  const showBadgeSupplyWarning =
    isDashboard && shouldShowBadgeSupplyWarning(roles, isOrganizer);

  const isLoading =
    isHackerLoading ||
    isNetworkingLoading ||
    isProfileLoading ||
    isProfileUpdating;

  useEffect(() => {
    if (hasInitializedBadgeName || isHackerLoading || isNetworkingLoading) {
      return;
    }

    setBadgeDisplayName(extendedProfile?.badge_display_name?.trim() ?? "");
    setHasInitializedBadgeName(true);
  }, [
    extendedProfile,
    hasInitializedBadgeName,
    isHackerLoading,
    isNetworkingLoading,
  ]);

  useEffect(() => {
    if (
      isDashboard ||
      !isResponseUpdated ||
      isHackerLoading ||
      hasSeededPrefill.current
    ) {
      return;
    }
    hasSeededPrefill.current = true;

    const current: BadgeNetworkingAnswers = {
      [Field.NETWORKING_PHONE]:
        (responsesState[Field.NETWORKING_PHONE] as string | null) ?? null,
      [Field.NETWORKING_EMAIL]:
        (responsesState[Field.NETWORKING_EMAIL] as string | null) ?? null,
      [Field.LINKEDIN]:
        (responsesState[Field.LINKEDIN] as string | null) ?? null,
      [Field.INSTAGRAM]:
        (responsesState[Field.INSTAGRAM] as string | null) ?? null,
      [Field.TWITTER]: (responsesState[Field.TWITTER] as string | null) ?? null,
      [Field.DISCORD_TAG]:
        (responsesState[Field.DISCORD_TAG] as string | null) ?? null,
    };

    const next = buildBadgeNetworkingValues(current, {
      personalPhone: (claimData[Field.PHONE_NUMBER] as string | null) ?? null,
      accountEmail: info?.email ?? null,
      longLinks: claimData[Field.LONG_LINKS] as
        | string[]
        | Record<string, string>
        | null,
      hasCompletedBadge:
        responsesState[Field.BADGE_RSVP_STAGE] === PageStage.COMPLETED,
    });

    for (const field of NETWORKING_PREFILL_FIELDS) {
      if (next[field] != null && next[field] !== current[field]) {
        setResponsesState(field, next[field]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResponseUpdated, isHackerLoading]);

  const previousStepsComplete = isHacker
    ? responsesState[Field.TRAVEL_RSVP_STAGE] === PageStage.COMPLETED
    : responsesState[Field.PERSONAL_RSVP_STAGE] === PageStage.COMPLETED;

  useEffect(() => {
    if (isDashboard) {
      return;
    }
    if (isValid && isMounted) {
      navigateNext();
    }
    if (!previousStepsComplete) {
      navigateBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting]);

  const onFieldChange = (field: Field, value: string) => {
    setIsBlocking(true);
    setResponsesState(field, value);
  };

  const onBadgeDisplayNameChange = (value: string) => {
    setIsBlocking(true);
    setBadgeDisplayName(value);
    queueAutosave("badge-display-name", async () => {
      const result = await autosaveExtendedProfile({
        interests: extendedProfile?.interests ?? "",
        badge_display_name: value.trim(),
      });
      if (result.errors) throw new Error(result.errors[0]?.message);
    });
  };

  const onSave = async () => {
    const trimmedBadgeDisplayName = badgeDisplayName.trim();
    const phone = String(responsesState[Field.NETWORKING_PHONE] ?? "");
    const email = String(responsesState[Field.NETWORKING_EMAIL] ?? "");
    const linkedIn = String(responsesState[Field.LINKEDIN] ?? "");
    const instagram = String(responsesState[Field.INSTAGRAM] ?? "");
    const twitter = String(responsesState[Field.TWITTER] ?? "");
    const discord = String(responsesState[Field.DISCORD_TAG] ?? "");
    const validationIssues = [
      {
        label: "Badge display name",
        isValid:
          Boolean(trimmedBadgeDisplayName) &&
          isLatinOnly(trimmedBadgeDisplayName),
      },
      {
        label: "Badge profile phone number",
        isValid: !phone || (isLatinOnly(phone) && isPhoneNumber(phone)),
      },
      {
        label: "Badge profile email",
        isValid: !email || (isLatinOnly(email) && isEmail(email)),
      },
      {
        label: "LinkedIn",
        isValid:
          !linkedIn ||
          (isLatinOnly(linkedIn) &&
            isValidBadgeSocialURL(linkedIn, "linkedin")),
      },
      {
        label: "Instagram",
        isValid:
          !instagram ||
          (isLatinOnly(instagram) &&
            isValidBadgeSocialURL(instagram, "instagram")),
      },
      {
        label: "X (Twitter)",
        isValid:
          !twitter ||
          (isLatinOnly(twitter) && isValidBadgeSocialURL(twitter, "x")),
      },
      {
        label: "Discord tag",
        isValid:
          !discord || (isLatinOnly(discord) && isValidDiscordTag(discord)),
      },
    ];
    const allFieldsValid = validationIssues.every(({ isValid }) => isValid);

    if (isValid && allFieldsValid) {
      try {
        cancelAutosave("badge-display-name");
        await waitForAutosave();
        const { errors: profileErrors } = await updateExtendedProfile({
          interests: extendedProfile?.interests ?? "",
          badge_display_name: trimmedBadgeDisplayName,
        });

        if (profileErrors) {
          throw new Error(
            "We weren't able to submit your answers, please try again."
          );
        }

        if (isHacker && !isDashboard) {
          const badgeStage = responsesState[Field.BADGE_RSVP_STAGE] as
            | string
            | null;

          if (
            shouldInitializeDirectoryProfile(badgeStage) &&
            directoryProfileLoadError
          ) {
            throw new Error(
              "We weren't able to prepare your Directory profile, please try again."
            );
          }

          const directorySeed = buildInitialDirectoryProfileSeed(
            directoryProfile,
            {
              preferredName: claimData[Field.PREFERRED_NAME] as string | null,
              pronouns: claimData[Field.PRONOUNS] as string[] | null,
              badgeContacts: {
                contact_linkedin: linkedIn,
                contact_discord: discord,
                contact_x: twitter,
              },
            },
            badgeStage
          );

          if (Object.keys(directorySeed).length > 0) {
            await upsertDiscoveryProfile({
              ...directorySeed,
              stage_slug: "draft",
            });
          }
        }

        const { errors } = await updateResponses({
          ...responsesState,
          [Field.BADGE_RSVP_STAGE]: PageStage.COMPLETED,
        });

        if (errors) {
          throw new Error(
            "We weren't able to submit your answers, please try again."
          );
        }

        successToast("Answers successfully saved!");
        setIsBlocking(false);
        if (!isDashboard) {
          setSubmitting(true);
        }
      } catch (e) {
        if (typeof e === "object") {
          errorToast((e as GraphQLError)?.message);
        } else {
          errorToast(e as string);
        }
      }
    } else {
      errorToast(formatValidationIssues(validationIssues));
      setShowErrors(true);
    }
  };

  const fieldError = (value: string, fallback: string | false) =>
    !isLatinOnly(value) ? LATIN_ONLY_MESSAGE : fallback;

  const phoneValue = responsesState[Field.NETWORKING_PHONE] ?? "";
  const emailValue = responsesState[Field.NETWORKING_EMAIL] ?? "";
  const linkedInValue = responsesState[Field.LINKEDIN] ?? "";
  const instagramValue = responsesState[Field.INSTAGRAM] ?? "";
  const twitterValue = responsesState[Field.TWITTER] ?? "";
  const discordValue = responsesState[Field.DISCORD_TAG] ?? "";

  const phoneInput = (
    <FlexColumn>
      <FormFieldLabelRow>
        <Text mods="bold textBody">Badge Profile: Phone Number</Text>
        <OptionalLabel>Optional</OptionalLabel>
      </FormFieldLabelRow>
      <InputWrapper
        type="text"
        placeholder="Enter phone number"
        value={phoneValue}
        onChange={(e: any) =>
          onFieldChange(Field.NETWORKING_PHONE, e.currentTarget.value)
        }
        error={fieldError(
          phoneValue,
          phoneValue !== "" &&
            !isPhoneNumber(phoneValue) &&
            "Please enter a valid phone number"
        )}
      />
    </FlexColumn>
  );

  const emailInput = (
    <FlexColumn>
      <FormFieldLabelRow>
        <Text mods="bold textBody">Badge Profile: Email</Text>
        <OptionalLabel>Optional</OptionalLabel>
      </FormFieldLabelRow>
      <InputWrapper
        type="text"
        placeholder="Enter email"
        value={emailValue}
        onChange={(e: any) =>
          onFieldChange(Field.NETWORKING_EMAIL, e.currentTarget.value)
        }
        error={fieldError(
          emailValue,
          emailValue !== "" &&
            !isEmail(emailValue) &&
            "Please enter a valid email"
        )}
      />
    </FlexColumn>
  );

  const linkedInInput = (
    <FlexColumn>
      <FormFieldLabelRow>
        <Text mods="bold textBody">Badge Profile: LinkedIn</Text>
        <OptionalLabel>Optional</OptionalLabel>
      </FormFieldLabelRow>
      <InputWrapper
        type="text"
        placeholder="Enter full URL"
        value={linkedInValue}
        onChange={(e: any) =>
          onFieldChange(Field.LINKEDIN, e.currentTarget.value)
        }
        error={fieldError(
          linkedInValue,
          !isValidSocialMediaURL(linkedInValue, "linkedin") &&
            "Please enter a valid LinkedIn URL"
        )}
      />
    </FlexColumn>
  );

  const instagramInput = (
    <FlexColumn>
      <FormFieldLabelRow>
        <Text mods="bold textBody">Badge Profile: Instagram</Text>
        <OptionalLabel>Optional</OptionalLabel>
      </FormFieldLabelRow>
      <InputWrapper
        type="text"
        placeholder="Enter full URL"
        value={instagramValue}
        onChange={(e: any) =>
          onFieldChange(Field.INSTAGRAM, e.currentTarget.value)
        }
        error={fieldError(
          instagramValue,
          !isValidSocialMediaURL(instagramValue, "instagram") &&
            "Please enter a valid Instagram URL"
        )}
      />
    </FlexColumn>
  );

  const twitterInput = (
    <FlexColumn>
      <FormFieldLabelRow>
        <Text mods="bold textBody">Badge Profile: Twitter/X</Text>
        <OptionalLabel>Optional</OptionalLabel>
      </FormFieldLabelRow>
      <InputWrapper
        type="text"
        placeholder="Enter full URL"
        value={twitterValue}
        onChange={(e: any) =>
          onFieldChange(Field.TWITTER, e.currentTarget.value)
        }
        error={fieldError(
          twitterValue,
          !isValidBadgeSocialURL(twitterValue, "x") &&
            "Please enter a valid Twitter/X URL"
        )}
      />
    </FlexColumn>
  );

  const discordInput = (
    <FlexColumn>
      <FormFieldLabelRow>
        <Text mods="bold textBody">Badge Profile: Discord Tag</Text>
        <OptionalLabel>Optional</OptionalLabel>
      </FormFieldLabelRow>
      <InputWrapper
        type="text"
        placeholder="Enter full tag"
        value={discordValue}
        onChange={(e: any) =>
          onFieldChange(Field.DISCORD_TAG, e.currentTarget.value)
        }
        error={fieldError(
          discordValue,
          discordValue !== "" &&
            !isValidDiscordTag(discordValue) &&
            "Please enter a valid Discord tag"
        )}
      />
    </FlexColumn>
  );

  if (!isDashboard && isVolunteer) {
    return <Navigate to={Route.HACKER_CONFIRMATION} replace />;
  }

  const pageContent = (
    <PageWrapper>
      <Prompt
        when={isBlocking && (isDashboard || hasUnsavedAutosave) && !submitting}
        message="You have unsaved changes, are you sure you want to move away from this page?"
      />
      {!isDashboard && isTabletOrSmaller && (
        <RSVPSidebar currentRoute="Your Badge" />
      )}
      {!isDashboard && <Heading1Text>Your Badge</Heading1Text>}
      {!isDashboard && (
        <Text mods="grey">
          All questions are required unless stated otherwise.
        </Text>
      )}
      {showBadgeSupplyWarning && (
        <>
          <Alert
            color="warning"
            icon={<ExclamationTriangleIcon width={20} height={20} />}
          >
            Due to supply limitations, we may not be able to provide you with a
            physical badge containing your custom information.
          </Alert>
          <Spacer height={24} />
        </>
      )}
      <Heading3Wrapper $compactTop={isDashboard}>
        <Text mods="h3 heading">Badge Display Name</Text>
      </Heading3Wrapper>
      <Text>
        Set your name as you would like it displayed on your badge! We recommend
        a first and last name, but it can be distinct from your legal name!
      </Text>
      <Spacer height={16} />
      {isTabletOrSmaller ? (
        <FlexColumn>
          <Text mods="bold textBody">Badge Display Name</Text>
          <InputWrapper
            type="text"
            placeholder="Enter badge display name"
            value={badgeDisplayName}
            onChange={(e: any) =>
              onBadgeDisplayNameChange(e.currentTarget.value)
            }
            error={fieldError(
              badgeDisplayName,
              showErrors &&
                !badgeDisplayName.trim() &&
                "Please enter a badge display name"
            )}
          />
        </FlexColumn>
      ) : (
        <FlexRow>
          <FlexColumn>
            <Text mods="bold textBody">Badge Display Name</Text>
            <InputWrapper
              type="text"
              placeholder="Enter badge display name"
              value={badgeDisplayName}
              onChange={(e: any) =>
                onBadgeDisplayNameChange(e.currentTarget.value)
              }
              error={
                showErrors &&
                !badgeDisplayName.trim() &&
                "Please enter a badge display name"
              }
            />
          </FlexColumn>
          <Spacer width={50} />
          <FlexColumn />
        </FlexRow>
      )}
      {isDashboard && (
        <>
          <Spacer height={24} />
          <Button
            color="primary"
            disabled={isLoading}
            onClick={onSave}
            size="md"
          >
            {isLoading ? <Spinner /> : <p>Save</p>}
          </Button>
        </>
      )}
      <Heading3Wrapper>
        <Text mods="h3 heading">Profile Links</Text>
      </Heading3Wrapper>
      <Text>
        Connecting with other hackers is one of the most rewarding parts of Hack
        the North! We challenge you to step out of your comfort zone, and say
        hello to at least 3 new people this weekend, you&rsquo;ll find
        they&rsquo;re all really cool, likeminded, and can become lifelong
        friends!
      </Text>
      <Spacer height={16} />
      <Text>
        To make that easier, this year we&rsquo;re giving you the chance to
        store your social media links inside your hacker badge that you&rsquo;ll
        receive at registration, and during the event, you&rsquo;ll be able to
        easily share your contact info with all your new friends!
      </Text>
      <Spacer height={16} />
      <Alert
        color="primary"
        icon={<InformationCircleIcon width={20} height={20} />}
      >
        Please note all these fields are <strong>optional</strong>, and any
        contacts you enter here <strong>will be shared</strong> with people you
        network with using your badge, so only put contact information
        you&rsquo;re comfortable sharing (which is why we&rsquo;re asking you
        again for this info).
      </Alert>
      <Spacer height={42} />
      {isTabletOrSmaller ? (
        <div>
          {phoneInput}
          {emailInput}
          {linkedInInput}
          {instagramInput}
          {twitterInput}
          {discordInput}
        </div>
      ) : (
        <div>
          <FlexRow>
            {phoneInput}
            <Spacer width={50} />
            {emailInput}
          </FlexRow>
          <FlexRow>
            {linkedInInput}
            <Spacer width={50} />
            {instagramInput}
          </FlexRow>
          <FlexRow>
            {twitterInput}
            <Spacer width={50} />
            {discordInput}
          </FlexRow>
        </div>
      )}
      <Spacer height={isDashboard ? 32 : 100} />
      {isDashboard && (
        <>
          <Text mods="grey">
            You can update your badge information at any time. Information
            you&rsquo;ve already shared will remain on other people&rsquo;s
            badges. After making changes, you&rsquo;ll need to synchronize your
            badge with the dashboard again. Open Sync under Hacker Badge,
            connect your badge, and press Sync to apply your saved changes.
          </Text>
          <Spacer height={16} />
        </>
      )}
      <FlexRow>
        <Button color="primary" disabled={isLoading} onClick={onSave} size="md">
          {isLoading ? (
            <Spinner />
          ) : isDashboard ? (
            <p>Save</p>
          ) : (
            <FlexRow>
              <p>
                {responsesState[Field.BADGE_RSVP_STAGE] === PageStage.COMPLETED
                  ? "Save"
                  : "Next"}
              </p>
              <Spacer width="8px" />
              <ArrowRight width={16} />
            </FlexRow>
          )}
        </Button>
        <Spacer width="32px" />
        {showErrors && (
          <ErrorText>
            Please fix the invalid contact information before
            {isDashboard ? " saving." : " proceeding to the next form page."}
          </ErrorText>
        )}
      </FlexRow>
      {!isDashboard && <Spacer height={24} />}
    </PageWrapper>
  );

  if (!isDashboard) {
    return pageContent;
  }

  return (
    <>
      <Header
        title="Badge Info"
        subtitle="Manage the information stored on your hacker badge"
      />
      {pageContent}
    </>
  );
};

const BadgePage: React.FC<BadgePageProps> = (props) =>
  props.variant === "dashboard" ? (
    <BadgePageContent {...props} />
  ) : (
    <ProfileContextProvider>
      <BadgePageContent {...props} />
    </ProfileContextProvider>
  );

const Heading1Text = styled.h1`
  margin-top: 0;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-weight: 900;
  font-size: 28px;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    font-size: 24px;
  `}
  ${({ theme }) => theme.mediaQueries.mobile`
    font-size: 20px;
  `}
`;

const Heading3Wrapper = styled.div<{ $compactTop?: boolean }>`
  display: flex;
  align-items: flex-end;
  margin-top: ${({ $compactTop }) => ($compactTop ? "0" : "42px")};
  margin-bottom: 24px;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.globalConstants.color.textDanger};
`;

const InputWrapper = styled(TextInput).attrs({ size: "md" })`
  margin-top: 12px;
  width: 100%;
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  width: 100%;
`;

export default BadgePage;
